import {FC, MouseEvent, useState} from 'react';
import {Link} from 'react-router-dom';
import {BadgeOutlined, ContentCopyOutlined} from '@mui/icons-material';
import {Box, IconButton, Popover, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import {useCan} from 'src/permissions/hooks';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive, ActionSheetIcons} from 'src/components/mobile/ActionSheet';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {MissingBexioPill} from 'src/components/common/MissingBexioPill';
import {useMissingBexio} from 'src/hooks/useMissingBexio';
import {RegistrationStatusEnum, UserTypeEnum} from 'src/enums';
import {NotifySuccess} from 'src/utils/notification';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {MenuButton} from 'src/components/tables/respnsiveTable/MenuButton';
import {EmployeeStatus} from 'src/components/EmployeesTable/EmployeeStatus/EmployeeStatus';
import {PaymentModeStatus} from 'src/components/EmployeesTable/PaymentModeStatus';
import {EditEmployee} from 'src/components/EmployeesTable/EditEmployee';
import {DeleteEmployee} from 'src/components/EmployeesTable/DeleteEmployee';
import {ResendRegistration} from 'src/components/users/ResendRegistration';
import {CompanyEmployeeItemCardProps} from '../CompanyEmployeesTable.types';
import {buildEmployeeLink} from '../CompanyEmployeesTable.tableConfig';
import {
    factLinkSx,
    iconBtnSx,
    idValueSx,
    menuContentSx,
    popoverSx,
    resendWrapperSx,
    titleLinkSx,
} from './CompanyEmployeeItemCard.sx';

export const CompanyEmployeeItemCard: FC<CompanyEmployeeItemCardProps> = ({
                                                                             employee,
                                                                             businessId,
                                                                             companyId,
                                                                         }) => {
    const intl = useIntl();
    // The same actions the desktop row offers. Each of them gates itself on its own permission and
    // renders nothing without it, so the menu is offered only when at least one would appear.
    const canEditEmployee = useCan('employees.update');
    const canDeleteEmployee = useCan('employees.delete');
    const canResendInvite = useCan('registration.resendInvite');
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const {
        id,
        consumptionPaymentMode,
        user: {emailAddress, created, registrationStatus},
    } = employee;

    const isPendingRegistration = registrationStatus === RegistrationStatusEnum.PENDING_HASH;
    // The warning the desktop row carries as a red name and a tooltip - neither of which survives a
    // phone. Through the same hook the row uses, so the two cannot come to different answers.
    const showMissingBexio = useMissingBexio({
        hasBexio: !!employee.user?.bexioDataList?.length,
        userType: UserTypeEnum.EMPLOYEE,
        paymentMode: consumptionPaymentMode,
    });
    const hasActions = canEditEmployee || canDeleteEmployee || (canResendInvite && isPendingRegistration);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const copyToClipboard = () => {
        copy(String(id), {debug: true});
        NotifySuccess(intl.formatMessage({id: 'copiedSuccesfully'}));
    };

    const displayName = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || emailAddress || '';

    // The same nodes for both widths. Split only so the sheet can put the destructive one below a
    // rule; the popover still renders them in the one column it always did.
    const primaryActions = (
        <>
            {isPendingRegistration && (
                <Box sx={resendWrapperSx}>
                    <ResendRegistration
                        row={{...employee.user, chargePointId: '', canDeleteUser: false} as any}/>
                </Box>
            )}
            <EditEmployee row={employee}/>
        </>
    );

    const deleteAction = <DeleteEmployee row={employee}/>;

    // The menu the card's head carries. The sheet and the popover are portalled, so sitting in the
    // head's row costs the layout nothing.
    const menuAction = hasActions ? (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<BadgeOutlined/>}
                    title={`${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || emailAddress || ''}
                    subtitle={emailAddress}
                >
                    <ActionSheetIcons>{primaryActions}</ActionSheetIcons>
                    <ActionSheetDestructive>
                        <ActionSheetIcons>{deleteAction}</ActionSheetIcons>
                    </ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Box sx={menuContentSx}>
                        {primaryActions}
                        {deleteAction}
                    </Box>
                </Popover>
            )}
        </>
    ) : undefined;

    return (
        <TableCard>
            <PhoneCardHead
                icon={<BadgeOutlined/>}
                person
                title={
                    <Link to={buildEmployeeLink(id, businessId, companyId)}>
                        <Typography
                            component="span"
                            sx={titleLinkSx}
                        >
                            {displayName}
                        </Typography>
                    </Link>
                }
                subtitle={emailAddress || 'N/A'}
                action={menuAction}
            />
            <Box sx={phonePillsRowSx}>
                <EmployeeStatus label={registrationStatus as RegistrationStatusEnum}/>
                <PaymentModeStatus label={consumptionPaymentMode}/>
                {showMissingBexio && <MissingBexioPill/>}
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'userId'}),
                        value: (
                            <Box
                                component="span"
                                sx={idValueSx}
                            >
                                <Link to={buildEmployeeLink(id, businessId, companyId)}>
                                    <Typography
                                        component="span"
                                        sx={factLinkSx}
                                    >
                                        {id}
                                    </Typography>
                                </Link>
                                <IconButton
                                    onClick={copyToClipboard}
                                    sx={iconBtnSx}
                                >
                                    <ContentCopyOutlined sx={{width: '0.75rem'}}/>
                                </IconButton>
                            </Box>
                        ),
                    },
                    {
                        label: intl.formatMessage({id: 'dateColumnHeader'}),
                        value: dayjs(created).format('DD/MM/YY'),
                    },
                ]}
            />
        </TableCard>
    );
};
